import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import OrderForm from "@/pages/OrderForm";
import OrderDetail from "@/pages/OrderDetail";
import ScreenshotAI from "@/pages/ScreenshotAI";
import Billing from "@/pages/Billing";
import Vehicles from "@/pages/Vehicles";
import Employees from "@/pages/Employees";
import PriceLists from "@/pages/PriceLists";
import Conflicts from "@/pages/Conflicts";
import ExportCenter from "@/pages/ExportCenter";
import UsersAdmin from "@/pages/UsersAdmin";
import AuditLog from "@/pages/AuditLog";
import MonthlyReport from "@/pages/MonthlyReport";
import BulkOrders from "@/pages/BulkOrders";
import ModuleSelect from "@/pages/ModuleSelect";
import TicketLayout from "@/components/TicketLayout";
import TicketPlaceholder from "@/pages/ticket/TicketPlaceholder";

function Protected({ children, adminOnly, layout = "leistung" }) {
  const { user, loading, isAdmin } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lädt…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/modules" replace />;
  if (layout === "ticket") return <TicketLayout>{children}</TicketLayout>;
  if (layout === "none") return children;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/modules" element={<Protected layout="none"><ModuleSelect /></Protected>} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/orders/new" element={<Protected><OrderForm /></Protected>} />
      <Route path="/orders/:id" element={<Protected><OrderDetail /></Protected>} />
      <Route path="/orders/:id/edit" element={<Protected><OrderForm /></Protected>} />
      <Route path="/ai" element={<Protected><ScreenshotAI /></Protected>} />
      <Route path="/billing" element={<Protected><Billing /></Protected>} />
      <Route path="/monthly" element={<Protected><MonthlyReport /></Protected>} />
      <Route path="/bulk" element={<Protected><BulkOrders /></Protected>} />
      <Route path="/vehicles" element={<Protected><Vehicles /></Protected>} />
      <Route path="/employees" element={<Protected><Employees /></Protected>} />
      <Route path="/pricelists" element={<Protected><PriceLists /></Protected>} />
      <Route path="/conflicts" element={<Protected><Conflicts /></Protected>} />
      <Route path="/export" element={<Protected><ExportCenter /></Protected>} />
      <Route path="/users" element={<Protected adminOnly><UsersAdmin /></Protected>} />
      <Route path="/audit" element={<Protected><AuditLog /></Protected>} />

      {/* Modul 2 — Ticket Disposition */}
      <Route path="/ticket" element={<Protected layout="ticket"><TicketPlaceholder pageKey="dashboard" /></Protected>} />
      <Route path="/ticket/tickets" element={<Protected layout="ticket"><TicketPlaceholder pageKey="tickets" /></Protected>} />
      <Route path="/ticket/kunden" element={<Protected layout="ticket"><TicketPlaceholder pageKey="kunden" /></Protected>} />
      <Route path="/ticket/termine" element={<Protected layout="ticket"><TicketPlaceholder pageKey="termine" /></Protected>} />
      <Route path="/ticket/partner" element={<Protected layout="ticket"><TicketPlaceholder pageKey="partner" /></Protected>} />
      <Route path="/ticket/kapazitaeten" element={<Protected layout="ticket"><TicketPlaceholder pageKey="kapazitaeten" /></Protected>} />
      <Route path="/ticket/abwesenheiten" element={<Protected layout="ticket"><TicketPlaceholder pageKey="abwesenheiten" /></Protected>} />
      <Route path="/ticket/mitarbeiter" element={<Protected layout="ticket"><TicketPlaceholder pageKey="mitarbeiter" /></Protected>} />
      <Route path="/ticket/berichte" element={<Protected layout="ticket"><TicketPlaceholder pageKey="berichte" /></Protected>} />
      <Route path="/ticket/audit" element={<Protected layout="ticket"><TicketPlaceholder pageKey="audit" /></Protected>} />

      <Route path="*" element={<Navigate to="/modules" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => { document.title = "SKALARTECH | Leistungsabrechnung"; }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
